import { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Sprout, LayoutDashboard, Key, Building2, LogOut, Plus, 
  CheckCircle2, XCircle, Users, Calendar, ChevronRight, Tag, Percent, Trash2,
  Palette, Upload, Save, Loader2, CreditCard, Mail, Settings, Eye, EyeOff, Clock, FileText,
  Menu, X, MessageSquare, Send, Receipt, Download, Filter, Euro, Edit, Megaphone, Sparkles, AlertTriangle, Info,
  Bug, Lightbulb, ListTodo, Play, CheckCheck, ArrowRight, LayoutList, UserCog
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { adminApi } from '../../lib/api';
import ImpersonationAuditPage from './pages/ImpersonationAuditPage';
import { useAuth } from '../../context/AuthContext';
import { FooterMinimal } from '../../components/Footer';
import NotificationBell from '../../components/NotificationBell';

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, description }) => (
  <Card className="card-hover">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-heading font-semibold mt-1">{value}</p>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
          <Icon className="w-6 h-6 text-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Dashboard Overview
function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminApi.getStats();
        setStats(response.data);
      } catch (error) {
        toast.error('Erreur lors du chargement des statistiques');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (isLoading) {
    return <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-32 bg-muted rounded-xl" />
        ))}
      </div>
    </div>;
  }

  return (
    <div className="space-y-8 stagger-children">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Tableau de bord Admin</h1>
        <p className="text-muted-foreground mt-1">Vue d'ensemble de la plateforme</p>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Entreprises" 
          value={stats?.total_companies || 0}
          icon={Building2}
          description="Entreprises inscrites"
        />
        <StatCard 
          title="Licences Actives" 
          value={stats?.active_licenses || 0}
          icon={Key}
          description={`${stats?.total_licenses || 0} au total`}
        />
        <StatCard 
          title="Widgets" 
          value={stats?.total_widgets || 0}
          icon={LayoutList}
          description="Widgets créés"
        />
        <StatCard 
          title="Clients" 
          value={stats?.total_clients || 0}
          icon={Users}
          description="Clients enregistrés"
        />
      </div>

      {/* Statistiques d'activité */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Activité</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title="Réservations" 
            value={(stats?.total_bookings || 0) + (stats?.total_punctual_bookings || 0)}
            icon={Calendar}
            description="Total des réservations"
          />
          <StatCard 
            title="Demandes en attente" 
            value={stats?.pending_requests || 0}
            icon={Clock}
            description="Abonnements + ponctuelles"
          />
          <StatCard 
            title="Demandes de devis" 
            value={stats?.total_quote_requests || 0}
            icon={FileText}
            description="RDV commerciaux"
          />
          <StatCard 
            title="Tickets ouverts" 
            value={stats?.open_tickets || 0}
            icon={MessageSquare}
            description="En attente de traitement"
          />
        </div>
      </div>

      {/* Roadmap & Tickets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Roadmap Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-primary" />
              Roadmap
            </CardTitle>
            <CardDescription>Suivi du développement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-3">
                  <Play className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-800">En cours</span>
                </div>
                <span className="text-2xl font-bold text-amber-600">{stats?.roadmap_stats?.in_progress || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <CheckCheck className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Terminés</span>
                </div>
                <span className="text-2xl font-bold text-green-600">{stats?.roadmap_stats?.completed || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Lightbulb className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">À faire</span>
                </div>
                <span className="text-2xl font-bold">
                  {(stats?.roadmap_stats?.total || 0) - (stats?.roadmap_stats?.in_progress || 0) - (stats?.roadmap_stats?.completed || 0)}
                </span>
              </div>
              <NavLink to="/admin/roadmap" className="group">
                <div className="flex items-center justify-center gap-2 p-3 mt-2 border border-dashed border-primary/30 rounded-lg text-primary hover:bg-primary/5 transition-colors">
                  <span className="text-sm font-medium">Voir la roadmap</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </NavLink>
            </div>
          </CardContent>
        </Card>

        {/* Recent Tickets */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Tickets récents
            </CardTitle>
            <CardDescription>Support en attente</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.recent_tickets && stats.recent_tickets.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_tickets.map((ticket) => (
                  <div key={ticket.id} className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{ticket.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{ticket.email}</p>
                    </div>
                    <Badge 
                      variant="outline"
                      className={`ml-2 text-xs ${
                        ticket.priority === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                        ticket.priority === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-gray-50 text-gray-700 border-gray-200'
                      }`}
                    >
                      {ticket.priority === 'high' ? 'Urgent' : ticket.priority === 'medium' ? 'Moyen' : 'Bas'}
                    </Badge>
                  </div>
                ))}
                <NavLink to="/admin/tickets" className="group">
                  <div className="flex items-center justify-center gap-2 p-3 mt-2 border border-dashed border-primary/30 rounded-lg text-primary hover:bg-primary/5 transition-colors">
                    <span className="text-sm font-medium">Voir tous les tickets</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </NavLink>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500" />
                <p className="text-sm">Aucun ticket en attente</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Actions rapides */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Actions Rapides</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <NavLink to="/admin/licenses" className="group">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors">
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-primary" />
                <span>Gérer les licences</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </div>
          </NavLink>
          <NavLink to="/admin/companies" className="group">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-primary" />
                <span>Voir les entreprises</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </div>
          </NavLink>
          <NavLink to="/admin/impersonation-audit" className="group">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors">
              <div className="flex items-center gap-3">
                <UserCog className="w-5 h-5 text-primary" />
                <span>Audit — Voir en tant que</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </div>
          </NavLink>
          <NavLink to="/admin/roadmap" className="group">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors">
              <div className="flex items-center gap-3">
                <ListTodo className="w-5 h-5 text-primary" />
                <span>Gérer la roadmap</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </div>
          </NavLink>
        </CardContent>
      </Card>
    </div>
  );
}

// Licenses Management
function LicensesPage() {
  const [licenses, setLicenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [licenseDetail, setLicenseDetail] = useState(null);
  const [promoValidation, setPromoValidation] = useState(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newLicense, setNewLicense] = useState({
    company_name: '',
    company_email: '',
    license_type: 'trial',
    promo_code: '',
  });
  const [editForm, setEditForm] = useState({
    trial_days_extend: 0,
    promo_code: '',
    notes: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'manual',
    notes: ''
  });

  const fetchLicenses = async () => {
    try {
      const response = await adminApi.getLicenses();
      setLicenses(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des licences');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, []);

  const openLicenseDetail = async (license) => {
    setSelectedLicense(license);
    try {
      const response = await adminApi.getLicenseDetail(license.id);
      setLicenseDetail(response.data);
      setEditForm({ trial_days_extend: 0, promo_code: '', notes: license.notes || '' });
      setPaymentForm({ amount: '', payment_method: 'manual', notes: '' });
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleValidatePromo = async (code) => {
    if (!code || code.length < 3) {
      setPromoValidation(null);
      return;
    }
    
    setIsValidatingPromo(true);
    try {
      const response = await adminApi.validatePromoCode(code);
      setPromoValidation(response.data);
    } catch (error) {
      setPromoValidation({ valid: false, error: error.response?.data?.detail || 'Code invalide' });
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleCreateLicense = async () => {
    if (!newLicense.company_name || !newLicense.company_email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const response = await adminApi.createLicense(newLicense);
      let message = `Licence créée: ${response.data.license_key}`;
      if (response.data.promo_applied) {
        message += ` (${response.data.discount_percent}% de réduction appliquée)`;
      }
      toast.success(message);
      setIsDialogOpen(false);
      setNewLicense({ company_name: '', company_email: '', license_type: 'trial', promo_code: '' });
      setPromoValidation(null);
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la licence');
    }
  };

  const handleToggleLicense = async (id, currentStatus) => {
    try {
      await adminApi.updateLicense(id, !currentStatus);
      toast.success('Licence mise à jour');
      fetchLicenses();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleExtendTrial = async () => {
    if (!editForm.trial_days_extend || editForm.trial_days_extend <= 0) {
      toast.error('Entrez un nombre de jours valide');
      return;
    }
    setIsSubmitting(true);
    try {
      await adminApi.patchLicense(selectedLicense.id, { trial_days_extend: editForm.trial_days_extend });
      toast.success(`Période d'essai prolongée de ${editForm.trial_days_extend} jours`);
      setEditForm(f => ({ ...f, trial_days_extend: 0 }));
      const response = await adminApi.getLicenseDetail(selectedLicense.id);
      setLicenseDetail(response.data);
      fetchLicenses();
    } catch (error) {
      toast.error('Erreur lors de la prolongation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!editForm.promo_code) {
      toast.error('Entrez un code promo');
      return;
    }
    setIsSubmitting(true);
    try {
      await adminApi.patchLicense(selectedLicense.id, { promo_code: editForm.promo_code });
      toast.success('Code promo appliqué');
      setEditForm(f => ({ ...f, promo_code: '' }));
      const response = await adminApi.getLicenseDetail(selectedLicense.id);
      setLicenseDetail(response.data);
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Entrez un montant valide');
      return;
    }
    setIsSubmitting(true);
    try {
      await adminApi.recordPayment(selectedLicense.id, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes
      });
      toast.success('Paiement enregistré');
      setPaymentForm({ amount: '', payment_method: 'manual', notes: '' });
      const response = await adminApi.getLicenseDetail(selectedLicense.id);
      setLicenseDetail(response.data);
      fetchLicenses();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConvertToAnnual = async () => {
    setIsSubmitting(true);
    try {
      await adminApi.patchLicense(selectedLicense.id, { license_type: 'annual' });
      toast.success('Licence convertie en annuelle');
      const response = await adminApi.getLicenseDetail(selectedLicense.id);
      setLicenseDetail(response.data);
      fetchLicenses();
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLicense = async (licenseId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette licence ? Cette action est irréversible.')) {
      return;
    }
    try {
      await adminApi.deleteLicense(licenseId);
      toast.success('Licence supprimée');
      setIsDetailOpen(false);
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR');
    } catch {
      return dateStr;
    }
  };

  const getDaysRemaining = (endDateStr) => {
    if (!endDateStr) return null;
    try {
      const end = new Date(endDateStr);
      const now = new Date();
      const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return diff;
    } catch {
      return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Gestion des Licences</h1>
          <p className="text-muted-foreground mt-1">Créez et gérez les licences des entreprises</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) setPromoValidation(null);
        }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="create-license-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Licence
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Créer une Licence</DialogTitle>
              <DialogDescription>
                Créez une nouvelle licence pour une entreprise
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nom de l'entreprise *</Label>
                <Input
                  data-testid="license-company-name"
                  placeholder="Jardinerie Dupont"
                  value={newLicense.company_name}
                  onChange={(e) => setNewLicense({ ...newLicense, company_name: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  data-testid="license-company-email"
                  type="email"
                  placeholder="contact@jardinerie.fr"
                  value={newLicense.company_email}
                  onChange={(e) => setNewLicense({ ...newLicense, company_email: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Type de licence</Label>
                <Select
                  value={newLicense.license_type}
                  onValueChange={(value) => setNewLicense({ ...newLicense, license_type: value })}
                >
                  <SelectTrigger data-testid="license-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Essai (15 jours)</SelectItem>
                    <SelectItem value="annual">Annuelle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Code Promo (optionnel)</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="license-promo-code"
                    placeholder="PROMO2024"
                    value={newLicense.promo_code}
                    onChange={(e) => {
                      const code = e.target.value.toUpperCase();
                      setNewLicense({ ...newLicense, promo_code: code });
                      // Debounce validation
                      clearTimeout(window.promoValidationTimeout);
                      window.promoValidationTimeout = setTimeout(() => handleValidatePromo(code), 500);
                    }}
                  />
                  {isValidatingPromo && (
                    <div className="flex items-center text-muted-foreground">
                      <span className="animate-spin">⏳</span>
                    </div>
                  )}
                </div>
                
                {promoValidation && (
                  <div className={`p-3 rounded-lg text-sm ${promoValidation.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    {promoValidation.valid ? (
                      <div className="text-green-700">
                        <div className="flex items-center gap-2 font-medium">
                          <CheckCircle2 className="w-4 h-4" />
                          Code valide : {promoValidation.discount_percent}% de réduction
                        </div>
                        {promoValidation.description && (
                          <p className="mt-1 text-green-600">{promoValidation.description}</p>
                        )}
                        {promoValidation.remaining_uses && (
                          <p className="mt-1 text-green-600">{promoValidation.remaining_uses} utilisations restantes</p>
                        )}
                      </div>
                    ) : (
                      <div className="text-red-700 flex items-center gap-2">
                        <XCircle className="w-4 h-4" />
                        {promoValidation.error}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <Button 
                className="w-full rounded-full" 
                data-testid="submit-license-btn"
                onClick={handleCreateLicense}
              >
                Créer la licence
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Chargement...</div>
          ) : licenses.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">Aucune licence créée</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entreprise</TableHead>
                  <TableHead>Clé de licence</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Promo</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licenses.map((license) => {
                  const daysRemaining = getDaysRemaining(license.trial_end_date || license.annual_end_date);
                  return (
                  <TableRow key={license.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openLicenseDetail(license)}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{license.company_name}</p>
                        <p className="text-sm text-muted-foreground">{license.company_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-sm bg-muted px-2 py-1 rounded">
                        {license.license_key?.substring(0, 12)}...
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={license.license_type === 'annual' ? 'default' : 'secondary'}>
                        {license.license_type === 'annual' ? 'Annuelle' : 'Essai'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {daysRemaining !== null ? (
                        <div className={`text-sm ${daysRemaining < 7 ? 'text-red-600 font-medium' : daysRemaining < 15 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                          {daysRemaining > 0 ? `${daysRemaining}j restants` : 'Expiré'}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {license.promo_code ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {license.promo_code} (-{license.promo_discount}%)
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={license.is_active ? 'default' : 'destructive'} className={license.is_active ? 'status-success' : ''}>
                        {license.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openLicenseDetail(license); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`toggle-license-${license.id}`}
                          onClick={(e) => { e.stopPropagation(); handleToggleLicense(license.id, license.is_active); }}
                        >
                          {license.is_active ? <XCircle className="w-4 h-4 text-destructive" /> : <CheckCircle2 className="w-4 h-4 text-success" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteLicense(license.id); }}
                          title="Supprimer la licence"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* License Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Détails de la licence</DialogTitle>
            <DialogDescription>{selectedLicense?.company_name}</DialogDescription>
          </DialogHeader>
          
          {licenseDetail && (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Informations</TabsTrigger>
                <TabsTrigger value="actions">Actions</TabsTrigger>
                <TabsTrigger value="payments">Paiements</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Clé de licence</p>
                    <code className="font-mono text-sm">{licenseDetail.license?.license_key}</code>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Type</p>
                    <Badge variant={licenseDetail.license?.license_type === 'annual' ? 'default' : 'secondary'}>
                      {licenseDetail.license?.license_type === 'annual' ? 'Annuelle' : 'Essai'}
                    </Badge>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{licenseDetail.license?.company_email}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Expiration</p>
                    <p className="font-medium">
                      {formatDate(licenseDetail.license?.trial_end_date || licenseDetail.license?.annual_end_date)}
                      {(() => {
                        const days = getDaysRemaining(licenseDetail.license?.trial_end_date || licenseDetail.license?.annual_end_date);
                        if (days !== null) {
                          return <span className={`ml-2 text-sm ${days < 7 ? 'text-red-600' : days < 15 ? 'text-amber-600' : 'text-green-600'}`}>
                            ({days > 0 ? `${days}j restants` : 'Expiré'})
                          </span>;
                        }
                        return null;
                      })()}
                    </p>
                  </div>
                  {licenseDetail.license?.promo_code && (
                    <div className="p-4 bg-green-50 rounded-lg col-span-2">
                      <p className="text-sm text-green-700">Code promo appliqué</p>
                      <p className="font-medium text-green-800">{licenseDetail.license.promo_code} (-{licenseDetail.license.promo_discount}%)</p>
                    </div>
                  )}
                  {licenseDetail.license?.notes && (
                    <div className="p-4 bg-muted/50 rounded-lg col-span-2">
                      <p className="text-sm text-muted-foreground">Notes</p>
                      <p>{licenseDetail.license.notes}</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="actions" className="space-y-4 mt-4">
                {/* Extend Trial */}
                {licenseDetail.license?.license_type === 'trial' && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Prolonger la période d'essai
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder="Jours à ajouter"
                          value={editForm.trial_days_extend}
                          onChange={e => setEditForm(f => ({ ...f, trial_days_extend: parseInt(e.target.value) || 0 }))}
                          className="w-32"
                        />
                        <Button onClick={handleExtendTrial} disabled={isSubmitting}>
                          Prolonger
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Apply Promo */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Tag className="w-4 h-4" /> Appliquer un code promo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Input
                        placeholder="CODE_PROMO"
                        value={editForm.promo_code}
                        onChange={e => setEditForm(f => ({ ...f, promo_code: e.target.value.toUpperCase() }))}
                        className="flex-1"
                      />
                      <Button onClick={handleApplyPromo} disabled={isSubmitting}>
                        Appliquer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Convert to Annual */}
                {licenseDetail.license?.license_type === 'trial' && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Key className="w-4 h-4" /> Convertir en licence annuelle
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-2">
                        Convertir cette licence d'essai en licence annuelle (365 jours à partir d'aujourd'hui)
                      </p>
                      <Button onClick={handleConvertToAnnual} disabled={isSubmitting}>
                        Convertir en annuelle
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              
              <TabsContent value="payments" className="space-y-4 mt-4">
                {/* Record Payment */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Enregistrer un paiement
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Montant (€)</Label>
                        <Input
                          type="number"
                          placeholder="299.00"
                          value={paymentForm.amount}
                          onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Méthode</Label>
                        <Select value={paymentForm.payment_method} onValueChange={v => setPaymentForm(f => ({ ...f, payment_method: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manual">Manuel</SelectItem>
                            <SelectItem value="stripe">Stripe</SelectItem>
                            <SelectItem value="bank_transfer">Virement</SelectItem>
                            <SelectItem value="check">Chèque</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input
                        placeholder="Référence, commentaire..."
                        value={paymentForm.notes}
                        onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                      />
                    </div>
                    <Button onClick={handleRecordPayment} disabled={isSubmitting} className="w-full">
                      Enregistrer le paiement
                    </Button>
                  </CardContent>
                </Card>
                
                {/* Payment History */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Historique des paiements</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {licenseDetail.payments?.length > 0 ? (
                      <div className="space-y-2">
                        {licenseDetail.payments.map(p => (
                          <div key={p.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div>
                              <p className="font-medium">{p.amount?.toFixed(2)}€</p>
                              <p className="text-xs text-muted-foreground">{p.payment_method} - {formatDate(p.payment_date)}</p>
                              {p.notes && <p className="text-xs text-muted-foreground">{p.notes}</p>}
                            </div>
                            <Badge variant="outline" className="bg-green-50 text-green-700">Payé</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">Aucun paiement enregistré</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Companies List
function CompaniesPage() {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const handleImpersonate = async (company) => {
    setImpersonatingId(company.id);
    try {
      const res = await adminApi.impersonateCompany(company.id);
      startImpersonation({
        token: res.data.token,
        role: res.data.role,
        company_id: res.data.company_id,
        target_name: res.data.target_name || company.name,
        session_id: res.data.session_id,
      });
      toast.success(`Connecté en tant que ${company.name}`);
      // Hard reload so the whole app re-hydrates with the impersonation
      // JWT (React state alone is not propagated to axios interceptors and
      // ProtectedRoute in time to prevent a bounce to /login).
      window.location.replace('/company');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible d\'accéder à cette entreprise');
    } finally {
      setImpersonatingId(null);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await adminApi.getCompanies();
      setCompanies(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des entreprises');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSuspend = async (companyId, currentlySuspended) => {
    try {
      await adminApi.suspendCompany(companyId, !currentlySuspended);
      toast.success(currentlySuspended ? 'Entreprise réactivée' : 'Entreprise suspendue');
      fetchCompanies();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleSendPasswordReset = async () => {
    if (!selectedCompany) return;
    setIsSendingReset(true);
    try {
      await adminApi.sendCompanyPasswordReset(selectedCompany.id);
      toast.success(`Email de réinitialisation envoyé à ${selectedCompany.email}`);
      setIsPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleChangePassword = async () => {
    if (!selectedCompany || !newPassword) return;
    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    setIsSaving(true);
    try {
      await adminApi.changeCompanyPassword(selectedCompany.id, newPassword);
      toast.success(`Mot de passe modifié pour ${selectedCompany.name}`);
      setIsPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setIsSaving(false);
    }
  };

  const openPasswordDialog = (company) => {
    setSelectedCompany(company);
    setNewPassword('');
    setIsPasswordDialogOpen(true);
  };

  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setEditForm({
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      siret: company.siret || '',
      website: company.website || '',
      description: company.description || '',
      license_type: company.license_type || company.license?.license_type || '',
      license_active: company.license_active !== undefined ? company.license_active : (company.license?.is_active ?? true),
      discount_percent: company.discount_percent || '',
      discount_end_date: company.discount_end_date || '',
      discount_reason: company.discount_reason || '',
      subscription_end_date: company.subscription_end_date ? company.subscription_end_date.slice(0, 10) : '',
      admin_notes: company.admin_notes || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveCompany = async () => {
    setIsSaving(true);
    try {
      const dataToSend = { ...editForm };
      // Convert empty strings to null for optional fields
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === '') {
          dataToSend[key] = null;
        }
      });
      // Convert discount_percent to number
      if (dataToSend.discount_percent) {
        dataToSend.discount_percent = parseFloat(dataToSend.discount_percent);
      }
      
      await adminApi.updateCompany(selectedCompany.id, dataToSend);
      toast.success('Entreprise mise à jour');
      setIsEditDialogOpen(false);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCompany = async () => {
    try {
      await adminApi.deleteCompany(selectedCompany.id);
      toast.success('Entreprise supprimée');
      setIsDeleteDialogOpen(false);
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Entreprises</h1>
        <p className="text-muted-foreground mt-1">Liste des entreprises inscrites sur la plateforme</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Chargement...</div>
          ) : companies.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">Aucune entreprise inscrite</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entreprise</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Licence</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Inscrit le</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.id} className={company.is_suspended ? 'bg-red-50/50' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${company.is_suspended ? 'bg-red-100' : 'bg-primary/10'}`}>
                          <Building2 className={`w-5 h-5 ${company.is_suspended ? 'text-red-600' : 'text-primary'}`} />
                        </div>
                        <div>
                          <p className="font-medium">{company.name}</p>
                          <p className="text-sm text-muted-foreground">{company.address || 'Adresse non renseignée'}</p>
                          {company.discount_percent > 0 && (
                            <Badge variant="outline" className="mt-1 bg-orange-50 text-orange-700 border-orange-200">
                              <Percent className="w-3 h-3 mr-1" />
                              -{company.discount_percent}% remise
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p>{company.email}</p>
                      <p className="text-sm text-muted-foreground">{company.phone || '-'}</p>
                    </TableCell>
                    <TableCell>
                      {company.license_type === 'trial' ? (
                        <div>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Clock className="w-3 h-3 mr-1" />
                            Essai
                          </Badge>
                          {company.trial_days_remaining !== undefined && (
                            <p className={`text-xs mt-1 ${company.is_trial_expired ? 'text-red-500' : 'text-muted-foreground'}`}>
                              {company.is_trial_expired 
                                ? 'Essai expiré' 
                                : `${company.trial_days_remaining}j restants`}
                            </p>
                          )}
                        </div>
                      ) : company.license_type === 'annual' ? (
                        <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                          <Key className="w-3 h-3 mr-1" />
                          Annuelle
                        </Badge>
                      ) : company.license_type === 'monthly' ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          <Key className="w-3 h-3 mr-1" />
                          Mensuelle
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500">
                          Aucune
                        </Badge>
                      )}
                      {company.license_active === false && company.license_type && (
                        <p className="text-xs text-red-500 mt-1">Inactive</p>
                      )}
                    </TableCell>
                    <TableCell>
                      {company.is_suspended ? (
                        <Badge variant="destructive" className="bg-red-100 text-red-700 hover:bg-red-100">
                          <XCircle className="w-3 h-3 mr-1" />
                          Suspendu
                        </Badge>
                      ) : (
                        <Badge variant="default" className="bg-green-100 text-green-700 hover:bg-green-100">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Actif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(company.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleImpersonate(company)}
                          disabled={impersonatingId === company.id}
                          title="Voir l'espace en tant que cette entreprise"
                          data-testid={`impersonate-company-${company.id}`}
                        >
                          {impersonatingId === company.id
                            ? <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                            : <UserCog className="w-4 h-4 text-amber-600" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(company)} title="Modifier">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleSuspend(company.id, company.is_suspended)}
                          title={company.is_suspended ? 'Réactiver' : 'Suspendre'}
                        >
                          {company.is_suspended ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-orange-600" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openPasswordDialog(company)}
                          title="Gérer le mot de passe"
                        >
                          <Key className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => { setSelectedCompany(company); setIsDeleteDialogOpen(true); }}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Company Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier l'entreprise</DialogTitle>
            <DialogDescription>{selectedCompany?.name}</DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="info" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="info">Informations</TabsTrigger>
              <TabsTrigger value="credentials">Identifiants</TabsTrigger>
              <TabsTrigger value="license">Licence</TabsTrigger>
              <TabsTrigger value="discount">Remise</TabsTrigger>
            </TabsList>
            
            <TabsContent value="info" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom de l'entreprise</Label>
                  <Input value={editForm.name || ''} onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={editForm.phone || ''} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} />
                </div>
                <div>
                  <Label>SIRET</Label>
                  <Input value={editForm.siret || ''} onChange={(e) => setEditForm({...editForm, siret: e.target.value})} />
                </div>
                <div>
                  <Label>Site web</Label>
                  <Input value={editForm.website || ''} onChange={(e) => setEditForm({...editForm, website: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>Adresse</Label>
                  <Input value={editForm.address || ''} onChange={(e) => setEditForm({...editForm, address: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea value={editForm.description || ''} onChange={(e) => setEditForm({...editForm, description: e.target.value})} rows={3} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="credentials" className="space-y-4 mt-4">
              <div className="p-4 border rounded-lg space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Email de connexion
                </h4>
                <div>
                  <Label>Email actuel</Label>
                  <Input 
                    type="email" 
                    value={editForm.email || ''} 
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})} 
                    placeholder="email@entreprise.fr"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Cet email sera utilisé pour la connexion de l'entreprise
                  </p>
                </div>
              </div>

              <div className="p-4 border rounded-lg space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Key className="w-4 h-4 text-primary" />
                  Mot de passe
                </h4>
                
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Option 1 : Envoyer un lien de réinitialisation à l'entreprise
                  </p>
                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      if (!selectedCompany) return;
                      try {
                        await adminApi.sendCompanyPasswordReset(selectedCompany.id);
                        toast.success(`Email envoyé à ${selectedCompany.email}`);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
                      }
                    }}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer le lien de réinitialisation
                  </Button>
                </div>

                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">OU</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Option 2 : Définir un nouveau mot de passe directement
                  </p>
                  <div className="flex gap-2">
                    <Input 
                      type="password" 
                      placeholder="Nouveau mot de passe (min. 8 caractères)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      type="button"
                      disabled={!newPassword || newPassword.length < 8}
                      onClick={async () => {
                        if (!selectedCompany || !newPassword) return;
                        try {
                          await adminApi.changeCompanyPassword(selectedCompany.id, newPassword);
                          toast.success(`Mot de passe modifié pour ${selectedCompany.name}`);
                          setNewPassword('');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Erreur');
                        }
                      }}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Appliquer
                    </Button>
                  </div>
                  {newPassword && newPassword.length < 8 && (
                    <p className="text-xs text-red-500">Le mot de passe doit contenir au moins 8 caractères</p>
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="license" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de licence</Label>
                  <Select value={editForm.license_type || ''} onValueChange={(v) => setEditForm({...editForm, license_type: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">Essai (15 jours)</SelectItem>
                      <SelectItem value="monthly">Mensuelle</SelectItem>
                      <SelectItem value="annual">Annuelle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut de la licence</Label>
                  <Select value={editForm.license_active ? 'active' : 'inactive'} onValueChange={(v) => setEditForm({...editForm, license_active: v === 'active'})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date de fin d'abonnement</Label>
                  <Input type="date" value={editForm.subscription_end_date || ''} onChange={(e) => setEditForm({...editForm, subscription_end_date: e.target.value})} />
                </div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Note :</strong> Modifier le type de licence mettra à jour la licence existante ou en créera une nouvelle si nécessaire.
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="discount" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Remise (%)</Label>
                  <Input 
                    type="number" 
                    min="0" 
                    max="100" 
                    value={editForm.discount_percent || ''} 
                    onChange={(e) => setEditForm({...editForm, discount_percent: e.target.value})}
                    placeholder="Ex: 20"
                  />
                </div>
                <div>
                  <Label>Date de fin de la remise</Label>
                  <Input type="date" value={editForm.discount_end_date || ''} onChange={(e) => setEditForm({...editForm, discount_end_date: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>Raison de la remise</Label>
                  <Input value={editForm.discount_reason || ''} onChange={(e) => setEditForm({...editForm, discount_reason: e.target.value})} placeholder="Ex: Client fidèle, partenariat..." />
                </div>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-700">
                  <strong>Remise accordée :</strong> Cette remise sera appliquée sur les prochaines factures de l'entreprise.
                </p>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="mt-4">
            <Label>Notes internes (admin)</Label>
            <Textarea 
              value={editForm.admin_notes || ''} 
              onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})} 
              placeholder="Notes visibles uniquement par les administrateurs..."
              rows={2}
            />
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveCompany} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer l'entreprise</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>{selectedCompany?.name}</strong> ?
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200 my-4">
            <p className="text-sm text-red-700">
              <strong>⚠️ Attention :</strong> Cette action est irréversible et supprimera :
            </p>
            <ul className="text-sm text-red-600 mt-2 list-disc list-inside">
              <li>Le compte de l'entreprise</li>
              <li>Tous les collaborateurs</li>
              <li>Tous les clients</li>
              <li>Toutes les réservations</li>
              <li>Tous les services</li>
              <li>Les widgets de réservation</li>
              <li>Les factures associées</li>
            </ul>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDeleteCompany}>
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer définitivement
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Management Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gestion du mot de passe</DialogTitle>
            <DialogDescription>
              {selectedCompany?.name} ({selectedCompany?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Option 1: Send reset email */}
            <div className="p-4 border rounded-lg space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                Envoyer un lien de réinitialisation
              </h4>
              <p className="text-sm text-muted-foreground">
                Un email sera envoyé à {selectedCompany?.email} avec un lien pour réinitialiser le mot de passe.
              </p>
              <Button 
                onClick={handleSendPasswordReset} 
                disabled={isSendingReset}
                className="w-full"
                variant="outline"
              >
                {isSendingReset ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Envoyer l'email</>
                )}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">OU</span>
              </div>
            </div>

            {/* Option 2: Change password directly */}
            <div className="p-4 border rounded-lg space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Key className="w-4 h-4 text-primary" />
                Changer le mot de passe directement
              </h4>
              <p className="text-sm text-muted-foreground">
                Définissez un nouveau mot de passe manuellement (minimum 8 caractères).
              </p>
              <div className="space-y-2">
                <Input 
                  type="password" 
                  placeholder="Nouveau mot de passe (min. 8 caractères)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <Button 
                  onClick={handleChangePassword} 
                  disabled={isSaving || !newPassword || newPassword.length < 8}
                  className="w-full"
                >
                  {isSaving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Modification...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Changer le mot de passe</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Promo Codes Management
function PromoCodesPage() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_percent: '',
    max_uses: '',
    valid_until: '',
    description: '',
  });

  const fetchPromoCodes = async () => {
    try {
      const response = await adminApi.getPromoCodes();
      setPromoCodes(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des codes promo');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const handleCreatePromo = async () => {
    if (!newPromo.code || !newPromo.discount_percent) {
      toast.error('Code et réduction sont obligatoires');
      return;
    }

    try {
      await adminApi.createPromoCode({
        code: newPromo.code,
        discount_percent: parseFloat(newPromo.discount_percent),
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        valid_until: newPromo.valid_until || null,
        description: newPromo.description || null,
      });
      toast.success('Code promo créé');
      setIsDialogOpen(false);
      setNewPromo({ code: '', discount_percent: '', max_uses: '', valid_until: '', description: '' });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleTogglePromo = async (id, currentStatus) => {
    try {
      await adminApi.updatePromoCode(id, !currentStatus);
      toast.success('Code promo mis à jour');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeletePromo = async (id) => {
    try {
      await adminApi.deletePromoCode(id);
      toast.success('Code promo supprimé');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Codes Promo</h1>
          <p className="text-muted-foreground mt-1">Gérez les codes promotionnels pour les licences</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="create-promo-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Code
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Créer un Code Promo</DialogTitle>
              <DialogDescription>
                Ce code pourra être utilisé lors de la création d'une licence
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  data-testid="promo-code-input"
                  placeholder="PROMO2024"
                  value={newPromo.code}
                  onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Réduction (%) *</Label>
                <Input
                  data-testid="promo-discount-input"
                  type="number"
                  min="1"
                  max="100"
                  placeholder="10"
                  value={newPromo.discount_percent}
                  onChange={(e) => setNewPromo({ ...newPromo, discount_percent: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Nombre d'utilisations max (optionnel)</Label>
                <Input
                  data-testid="promo-max-uses-input"
                  type="number"
                  min="1"
                  placeholder="100"
                  value={newPromo.max_uses}
                  onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Date d'expiration (optionnel)</Label>
                <Input
                  data-testid="promo-valid-until-input"
                  type="date"
                  value={newPromo.valid_until}
                  onChange={(e) => setNewPromo({ ...newPromo, valid_until: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Input
                  data-testid="promo-description-input"
                  placeholder="Offre de lancement"
                  value={newPromo.description}
                  onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })}
                />
              </div>
              
              <Button 
                className="w-full rounded-full" 
                data-testid="submit-promo-btn"
                onClick={handleCreatePromo}
              >
                Créer le code promo
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Chargement...</div>
          ) : promoCodes.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">Aucun code promo créé</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Réduction</TableHead>
                  <TableHead>Utilisation</TableHead>
                  <TableHead>Validité</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promoCodes.map((promo) => (
                  <TableRow key={promo.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-primary" />
                        <code className="font-mono text-sm bg-muted px-2 py-1 rounded">
                          {promo.code}
                        </code>
                      </div>
                      {promo.description && (
                        <p className="text-xs text-muted-foreground mt-1">{promo.description}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        <Percent className="w-3 h-3 mr-1" />
                        {promo.discount_percent}%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {promo.max_uses ? (
                        <span>{promo.times_used || 0} / {promo.max_uses}</span>
                      ) : (
                        <span>{promo.times_used || 0} (illimité)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {promo.valid_until ? (
                        new Date(promo.valid_until).toLocaleDateString('fr-FR')
                      ) : (
                        <span className="text-muted-foreground">Illimitée</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={promo.is_active ? 'default' : 'destructive'} className={promo.is_active ? 'status-success' : ''}>
                        {promo.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`toggle-promo-${promo.id}`}
                          onClick={() => handleTogglePromo(promo.id, promo.is_active)}
                        >
                          {promo.is_active ? <XCircle className="w-4 h-4 text-destructive" /> : <CheckCircle2 className="w-4 h-4 text-green-600" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-promo-${promo.id}`}
                          onClick={() => handleDeletePromo(promo.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Invoices / Billing Page
function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [config, setConfig] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [newInvoice, setNewInvoice] = useState({
    company_id: '',
    license_type: 'monthly',
    amount_ht: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesRes, companiesRes, configRes] = await Promise.all([
        adminApi.getInvoices(),
        adminApi.getCompanies(),
        adminApi.getConfig()
      ]);
      setInvoices(invoicesRes.data);
      setCompanies(companiesRes.data);
      setConfig(configRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    try {
      const data = {
        company_id: newInvoice.company_id,
        license_type: newInvoice.license_type,
        notes: newInvoice.notes || null
      };
      if (newInvoice.amount_ht) {
        data.amount_ht = parseFloat(newInvoice.amount_ht);
      }
      await adminApi.createInvoice(data);
      toast.success('Facture créée');
      setIsCreateDialogOpen(false);
      setNewInvoice({ company_id: '', license_type: 'monthly', amount_ht: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await adminApi.markInvoicePaid(invoiceId);
      toast.success('Facture marquée comme payée');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await adminApi.deleteInvoice(invoiceId);
      toast.success('Facture supprimée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await adminApi.sendInvoice(invoiceId);
      toast.success('Facture envoyée par email');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleViewPdf = async (invoice) => {
    try {
      // First, generate the PDF if it doesn't exist
      const response = await adminApi.getInvoicePdf(invoice.id);
      if (response.data.pdf_url) {
        // pdf_url already starts with /api, so we just need BACKEND_URL
        const url = `${process.env.REACT_APP_BACKEND_URL}${response.data.pdf_url}`;
        window.open(url, '_blank');
      } else {
        toast.error('Erreur lors de la génération du PDF');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleUpdateBillingConfig = async () => {
    try {
      await adminApi.updateConfig({
        billing_company_name: config.billing_company_name,
        billing_address: config.billing_address,
        billing_siret: config.billing_siret,
        billing_tva_number: config.billing_tva_number,
        billing_email: config.billing_email,
        billing_phone: config.billing_phone,
        invoice_prefix: config.invoice_prefix,
        invoice_next_number: parseInt(config.invoice_next_number) || 1,
        invoice_tva_rate: parseFloat(config.invoice_tva_rate) || 20
      });
      toast.success('Configuration sauvegardée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const filteredInvoices = filterStatus === 'all' 
    ? invoices 
    : invoices.filter(inv => inv.payment_status === filterStatus);

  const stats = {
    total: invoices.length,
    paid: invoices.filter(i => i.payment_status === 'paid').length,
    pending: invoices.filter(i => i.payment_status === 'pending').length,
    totalAmount: invoices.filter(i => i.payment_status === 'paid').reduce((sum, i) => sum + (i.amount_ttc || 0), 0)
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Facturation</h1>
          <p className="text-muted-foreground">Gestion des factures de licences</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-2" /> Nouvelle Facture</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer une facture</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Entreprise</Label>
                <Select value={newInvoice.company_id} onValueChange={(v) => setNewInvoice({...newInvoice, company_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une entreprise" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type de licence</Label>
                <Select value={newInvoice.license_type} onValueChange={(v) => setNewInvoice({...newInvoice, license_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensuelle ({config.monthly_license_price || 49}€ HT)</SelectItem>
                    <SelectItem value="annual">Annuelle ({config.annual_license_price || 490}€ HT)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Montant HT personnalisé (optionnel)</Label>
                <Input 
                  type="number" 
                  placeholder="Laisser vide pour prix par défaut"
                  value={newInvoice.amount_ht}
                  onChange={(e) => setNewInvoice({...newInvoice, amount_ht: e.target.value})}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea 
                  placeholder="Notes internes..."
                  value={newInvoice.notes}
                  onChange={(e) => setNewInvoice({...newInvoice, notes: e.target.value})}
                />
              </div>
              <Button onClick={handleCreateInvoice} disabled={!newInvoice.company_id} className="w-full">
                Créer la facture
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-sm text-muted-foreground">Total factures</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
            <p className="text-sm text-muted-foreground">Payées</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
            <p className="text-sm text-muted-foreground">En attente</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-primary">{stats.totalAmount.toFixed(2)}€</div>
            <p className="text-sm text-muted-foreground">CA encaissé</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList>
          <TabsTrigger value="invoices">Factures</TabsTrigger>
          <TabsTrigger value="settings">Paramètres de facturation</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Liste des factures</CardTitle>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="paid">Payées</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {filteredInvoices.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune facture</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N° Facture</TableHead>
                      <TableHead>Entreprise</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Montant TTC</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map(invoice => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-mono font-medium">{invoice.invoice_number}</TableCell>
                        <TableCell>{invoice.company_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {invoice.license_type === 'annual' ? 'Annuelle' : 'Mensuelle'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold">{invoice.amount_ttc?.toFixed(2)}€</TableCell>
                        <TableCell>
                          <Badge className={invoice.payment_status === 'paid' ? 'bg-green-500' : 'bg-orange-500'}>
                            {invoice.payment_status === 'paid' ? 'Payée' : 'En attente'}
                          </Badge>
                        </TableCell>
                        <TableCell>{invoice.created_at?.slice(0, 10)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleViewPdf(invoice)} title="Voir la facture">
                              <Eye className="w-4 h-4" />
                            </Button>
                            {invoice.payment_status !== 'paid' && (
                              <>
                                <Button variant="ghost" size="icon" onClick={() => handleMarkPaid(invoice.id)} title="Marquer payée">
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(invoice.id)} title="Supprimer">
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => handleSendInvoice(invoice.id)} title="Envoyer par email">
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Coordonnées de facturation</CardTitle>
              <CardDescription>Ces informations apparaîtront sur toutes les factures générées</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Raison sociale</Label>
                  <Input 
                    value={config.billing_company_name || ''} 
                    onChange={(e) => setConfig({...config, billing_company_name: e.target.value})}
                    placeholder="MSC RW SAS"
                  />
                </div>
                <div>
                  <Label>SIRET</Label>
                  <Input 
                    value={config.billing_siret || ''} 
                    onChange={(e) => setConfig({...config, billing_siret: e.target.value})}
                    placeholder="123 456 789 00012"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Adresse complète</Label>
                  <Input 
                    value={config.billing_address || ''} 
                    onChange={(e) => setConfig({...config, billing_address: e.target.value})}
                    placeholder="123 rue Example, 75001 Paris"
                  />
                </div>
                <div>
                  <Label>N° TVA Intracommunautaire</Label>
                  <Input 
                    value={config.billing_tva_number || ''} 
                    onChange={(e) => setConfig({...config, billing_tva_number: e.target.value})}
                    placeholder="FR12345678901"
                  />
                </div>
                <div>
                  <Label>Taux de TVA (%)</Label>
                  <Input 
                    type="number"
                    value={config.invoice_tva_rate || 20} 
                    onChange={(e) => setConfig({...config, invoice_tva_rate: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Email de facturation</Label>
                  <Input 
                    type="email"
                    value={config.billing_email || ''} 
                    onChange={(e) => setConfig({...config, billing_email: e.target.value})}
                    placeholder="facturation@resawork.fr"
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input 
                    value={config.billing_phone || ''} 
                    onChange={(e) => setConfig({...config, billing_phone: e.target.value})}
                    placeholder="01 23 45 67 89"
                  />
                </div>
              </div>

              <div className="pt-4 border-t">
                <h3 className="font-medium mb-4">Nomenclature des factures</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Préfixe</Label>
                    <Input 
                      value={config.invoice_prefix || 'MSCRW-fact'} 
                      onChange={(e) => setConfig({...config, invoice_prefix: e.target.value})}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Ex: MSCRW-fact → MSCRW-fact00001</p>
                  </div>
                  <div>
                    <Label>Prochain numéro</Label>
                    <Input 
                      type="number"
                      value={config.invoice_next_number || 1} 
                      onChange={(e) => setConfig({...config, invoice_next_number: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleUpdateBillingConfig} className="w-full md:w-auto">
                <Save className="w-4 h-4 mr-2" /> Sauvegarder les paramètres
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Branding / Customization Page
function BrandingPage() {
  const [settings, setSettings] = useState({
    app_name: 'ResaWork',
    logo_url: '',
    primary_color: '#8BC34A',
    secondary_color: '#FFFFFF',
    bg_color: '#F9F8F6',
    text_color: '#1a1a1a',
    footer_text: '© ResaWork'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [previewLogo, setPreviewLogo] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await adminApi.getSettings();
        setSettings(response.data);
        if (response.data.logo_url) {
          setPreviewLogo(response.data.logo_url);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await adminApi.updateSettings(settings);
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewLogo(reader.result);
        setSettings({ ...settings, logo_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Personnalisation</h1>
          <p className="text-muted-foreground mt-1">Personnalisez le logo et les couleurs de ResaWork</p>
        </div>
        <Button className="rounded-full" onClick={handleSave} disabled={isSaving} data-testid="save-settings-btn">
          {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Settings */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Identité</CardTitle>
              <CardDescription>Nom et logo de l'application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Nom de l'application</Label>
                <Input
                  data-testid="app-name-input"
                  value={settings.app_name}
                  onChange={(e) => setSettings({ ...settings, app_name: e.target.value })}
                  placeholder="ResaWork"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 border-2 border-dashed rounded-xl flex items-center justify-center bg-muted/50 overflow-hidden">
                    {previewLogo ? (
                      <img src={previewLogo} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <Upload className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      data-testid="logo-upload"
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-muted-foreground mt-1">PNG, JPG ou SVG. Max 500KB.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Texte du footer</Label>
                <Input
                  data-testid="footer-text-input"
                  value={settings.footer_text}
                  onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                  placeholder="Solution développée par..."
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Couleurs</CardTitle>
              <CardDescription>Palette de couleurs de l'application</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Couleur primaire</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                      className="w-12 h-10 rounded-lg border cursor-pointer"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                      className="font-mono text-sm flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Couleur secondaire</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                      className="w-12 h-10 rounded-lg border cursor-pointer"
                    />
                    <Input
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                      className="font-mono text-sm flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Couleur de fond</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={settings.bg_color}
                      onChange={(e) => setSettings({ ...settings, bg_color: e.target.value })}
                      className="w-12 h-10 rounded-lg border cursor-pointer"
                    />
                    <Input
                      value={settings.bg_color}
                      onChange={(e) => setSettings({ ...settings, bg_color: e.target.value })}
                      className="font-mono text-sm flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Couleur du texte</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={settings.text_color}
                      onChange={(e) => setSettings({ ...settings, text_color: e.target.value })}
                      className="w-12 h-10 rounded-lg border cursor-pointer"
                    />
                    <Input
                      value={settings.text_color}
                      onChange={(e) => setSettings({ ...settings, text_color: e.target.value })}
                      className="font-mono text-sm flex-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Preview */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Aperçu</CardTitle>
              <CardDescription>Prévisualisation des modifications</CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="rounded-xl p-6 min-h-[300px]"
                style={{ backgroundColor: settings.bg_color, color: settings.text_color }}
              >
                {/* Header Preview */}
                <div className="flex items-center gap-3 mb-6">
                  {previewLogo ? (
                    <img src={previewLogo} alt="Logo" className="w-10 h-10 object-contain" />
                  ) : (
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: settings.primary_color }}
                    >
                      <Sprout className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <span className="font-heading font-semibold text-xl">{settings.app_name}</span>
                </div>

                {/* Buttons Preview */}
                <div className="space-y-3 mb-6">
                  <button 
                    className="w-full py-2 px-4 rounded-full font-medium text-white"
                    style={{ backgroundColor: settings.primary_color }}
                  >
                    Bouton primaire
                  </button>
                  <button 
                    className="w-full py-2 px-4 rounded-full font-medium text-white"
                    style={{ backgroundColor: settings.secondary_color }}
                  >
                    Bouton secondaire
                  </button>
                </div>

                {/* Card Preview */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm mb-4">
                  <p className="font-medium" style={{ color: settings.text_color }}>Exemple de carte</p>
                  <p className="text-sm opacity-70">Contenu de la carte</p>
                </div>

                {/* Footer Preview */}
                <div className="text-center text-sm opacity-60 mt-4">
                  {settings.footer_text}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Configuration Page (Stripe, SMTP, Trial, Pricing)
function ConfigurationPage() {
  const [config, setConfig] = useState({
    // Stripe
    stripe_enabled: false,
    stripe_public_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    // SMTP
    smtp_enabled: false,
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'ResaWork',
    // Trial
    trial_enabled: true,
    trial_days: 15,
    // License Pricing
    monthly_license_price: 49,
    annual_license_price: 490,
    // Email Templates
    email_templates: {
      booking_confirmation: {
        subject: 'Confirmation de votre réservation - {{company_name}}',
        body: `<h2>Bonjour {{client_name}},</h2>
<p>Votre réservation a bien été confirmée !</p>
<p><strong>Détails :</strong></p>
<ul>
  <li>Service : {{service_name}}</li>
  <li>Date : {{date}}</li>
  <li>Heure : {{time}}</li>
  <li>Prix : {{price}}€</li>
</ul>
<p>Merci de votre confiance !</p>
<p>— L'équipe {{company_name}}</p>`
      },
      booking_reminder: {
        subject: 'Rappel : Votre intervention demain - {{company_name}}',
        body: `<h2>Bonjour {{client_name}},</h2>
<p>Nous vous rappelons votre intervention prévue demain.</p>
<p><strong>Détails :</strong></p>
<ul>
  <li>Service : {{service_name}}</li>
  <li>Date : {{date}}</li>
  <li>Heure : {{time}}</li>
</ul>
<p>À demain !</p>
<p>— L'équipe {{company_name}}</p>`
      },
      booking_cancelled: {
        subject: 'Annulation de votre réservation - {{company_name}}',
        body: `<h2>Bonjour {{client_name}},</h2>
<p>Votre réservation du {{date}} a été annulée.</p>
<p>Si vous n'êtes pas à l'origine de cette annulation, contactez-nous.</p>
<p>— L'équipe {{company_name}}</p>`
      },
      welcome_client: {
        subject: 'Bienvenue chez {{company_name}} !',
        body: `<h2>Bienvenue {{client_name}} !</h2>
<p>Votre compte client a été créé avec succès.</p>
<p>Vous pouvez dès maintenant accéder à votre espace personnel pour :</p>
<ul>
  <li>Consulter vos réservations</li>
  <li>Voir votre solde de crédits</li>
  <li>Réserver de nouveaux créneaux</li>
</ul>
<p>À très bientôt !</p>
<p>— L'équipe {{company_name}}</p>`
      }
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('booking_confirmation');
  
  const templateLabels = {
    booking_confirmation: { label: 'Confirmation de réservation', icon: '✅' },
    booking_reminder: { label: 'Rappel de rendez-vous', icon: '🔔' },
    booking_cancelled: { label: 'Annulation de réservation', icon: '❌' },
    welcome_client: { label: 'Bienvenue client', icon: '👋' },
  };
  
  const templateVariables = [
    '{{client_name}}', '{{client_email}}', '{{company_name}}', 
    '{{service_name}}', '{{date}}', '{{time}}', '{{price}}'
  ];

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await adminApi.getConfig();
        if (response.data) {
          setConfig(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.error('Error loading config:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await adminApi.updateConfig(config);
      toast.success('Configuration enregistrée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const testSmtp = async () => {
    try {
      await adminApi.testSmtp();
      toast.success('Email de test envoyé avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du test');
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Configuration</h1>
          <p className="text-muted-foreground mt-1">Paiement, emails et périodes d'essai</p>
        </div>
        <Button className="rounded-full" onClick={handleSave} disabled={isSaving} data-testid="save-config-btn">
          {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      <Tabs defaultValue="stripe" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="stripe" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Stripe
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Tarifs
          </TabsTrigger>
          <TabsTrigger value="smtp" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            SMTP
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="trial" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Période d'essai
          </TabsTrigger>
        </TabsList>

        {/* Stripe Configuration */}
        <TabsContent value="stripe">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    Configuration Stripe
                  </CardTitle>
                  <CardDescription>Configurez le paiement en ligne pour les réservations ponctuelles</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="stripe-enabled">Activer</Label>
                  <Switch
                    id="stripe-enabled"
                    checked={config.stripe_enabled}
                    onCheckedChange={(checked) => setConfig({ ...config, stripe_enabled: checked })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>Comment obtenir vos clés Stripe ?</strong><br />
                  Connectez-vous à votre <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Dashboard Stripe</a> → Développeurs → Clés API
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Clé publique (pk_...)</Label>
                <Input
                  data-testid="stripe-public-key"
                  placeholder="pk_test_..."
                  value={config.stripe_public_key}
                  onChange={(e) => setConfig({ ...config, stripe_public_key: e.target.value })}
                  disabled={!config.stripe_enabled}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Clé secrète (sk_...)</Label>
                <div className="relative">
                  <Input
                    data-testid="stripe-secret-key"
                    type={showStripeSecret ? 'text' : 'password'}
                    placeholder="sk_test_..."
                    value={config.stripe_secret_key}
                    onChange={(e) => setConfig({ ...config, stripe_secret_key: e.target.value })}
                    disabled={!config.stripe_enabled}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowStripeSecret(!showStripeSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showStripeSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Webhook Secret (whsec_...) - Optionnel</Label>
                <Input
                  data-testid="stripe-webhook-secret"
                  type="password"
                  placeholder="whsec_..."
                  value={config.stripe_webhook_secret}
                  onChange={(e) => setConfig({ ...config, stripe_webhook_secret: e.target.value })}
                  disabled={!config.stripe_enabled}
                />
                <p className="text-xs text-muted-foreground">Requis pour les webhooks de confirmation de paiement</p>
              </div>

              {config.stripe_enabled && config.stripe_public_key && config.stripe_secret_key && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700">Stripe est configuré et prêt à l'emploi</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* License Pricing Configuration */}
        <TabsContent value="pricing">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                Tarification des licences
              </CardTitle>
              <CardDescription>
                Définissez les prix des abonnements ResaWork. Ces prix seront affichés sur le site et dans les comptes entreprise.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Monthly Price */}
                <div className="space-y-4 p-6 rounded-xl border-2 border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Abonnement Mensuel</h3>
                      <p className="text-sm text-muted-foreground">Facturation chaque mois</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Prix mensuel (€)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid="monthly-price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={config.monthly_license_price}
                        onChange={(e) => setConfig({ ...config, monthly_license_price: parseFloat(e.target.value) || 0 })}
                        className="w-32"
                      />
                      <span className="text-lg font-semibold">€ / mois</span>
                    </div>
                  </div>
                </div>

                {/* Annual Price */}
                <div className="space-y-4 p-6 rounded-xl border-2 border-primary/30 bg-primary/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Abonnement Annuel</h3>
                      <p className="text-sm text-muted-foreground">Facturation annuelle</p>
                    </div>
                    <Badge className="ml-auto">Recommandé</Badge>
                  </div>
                  <div className="space-y-2">
                    <Label>Prix annuel (€)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid="annual-price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={config.annual_license_price}
                        onChange={(e) => setConfig({ ...config, annual_license_price: parseFloat(e.target.value) || 0 })}
                        className="w-32"
                      />
                      <span className="text-lg font-semibold">€ / an</span>
                    </div>
                  </div>
                  {config.monthly_license_price > 0 && config.annual_license_price > 0 && (
                    <div className="text-sm text-primary">
                      Économie : {((config.monthly_license_price * 12 - config.annual_license_price) / (config.monthly_license_price * 12) * 100).toFixed(0)}% 
                      ({(config.monthly_license_price * 12 - config.annual_license_price).toFixed(0)}€/an)
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-700">
                  Ces prix seront automatiquement mis à jour sur le site de présentation et dans les espaces entreprise.
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP Configuration */}
        <TabsContent value="smtp">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    Configuration SMTP
                  </CardTitle>
                  <CardDescription>Configurez l'envoi d'emails transactionnels</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="smtp-enabled">Activer</Label>
                  <Switch
                    id="smtp-enabled"
                    checked={config.smtp_enabled}
                    onCheckedChange={(checked) => setConfig({ ...config, smtp_enabled: checked })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>Configuration PlanetHoster</strong><br />
                  Serveur: mail.votredomaine.com | Port: 587 | Sécurité: TLS
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Serveur SMTP</Label>
                  <Input
                    data-testid="smtp-host"
                    placeholder="mail.votredomaine.com"
                    value={config.smtp_host}
                    onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                    disabled={!config.smtp_enabled}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Select 
                    value={config.smtp_port} 
                    onValueChange={(value) => setConfig({ ...config, smtp_port: value })}
                    disabled={!config.smtp_enabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25 (Non sécurisé)</SelectItem>
                      <SelectItem value="465">465 (SSL)</SelectItem>
                      <SelectItem value="587">587 (TLS - Recommandé)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Utilisateur</Label>
                  <Input
                    data-testid="smtp-user"
                    placeholder="contact@votredomaine.com"
                    value={config.smtp_user}
                    onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                    disabled={!config.smtp_enabled}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Mot de passe</Label>
                  <div className="relative">
                    <Input
                      data-testid="smtp-password"
                      type={showSmtpPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={config.smtp_password}
                      onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                      disabled={!config.smtp_enabled}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email d'expéditeur</Label>
                  <Input
                    data-testid="smtp-from-email"
                    placeholder="noreply@votredomaine.com"
                    value={config.smtp_from_email}
                    onChange={(e) => setConfig({ ...config, smtp_from_email: e.target.value })}
                    disabled={!config.smtp_enabled}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Nom d'expéditeur</Label>
                  <Input
                    data-testid="smtp-from-name"
                    placeholder="ResaWork"
                    value={config.smtp_from_name}
                    onChange={(e) => setConfig({ ...config, smtp_from_name: e.target.value })}
                    disabled={!config.smtp_enabled}
                  />
                </div>
              </div>

              {config.smtp_enabled && (
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={testSmtp}
                  disabled={!config.smtp_host || !config.smtp_user}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Envoyer un email de test
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Templates Configuration */}
        <TabsContent value="templates">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Template Selector */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Types d'emails</CardTitle>
                <CardDescription>Sélectionnez un template à personnaliser</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(templateLabels).map(([key, { label, icon }]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedTemplate(key)}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${
                      selectedTemplate === key 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{icon}</span>
                      <span className="font-medium text-sm">{label}</span>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Template Editor */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  {templateLabels[selectedTemplate]?.label}
                </CardTitle>
                <CardDescription>Personnalisez le contenu de l'email</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Variables disponibles */}
                <div className="p-3 bg-muted rounded-xl">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Variables disponibles :</p>
                  <div className="flex flex-wrap gap-1">
                    {templateVariables.map((v) => (
                      <code 
                        key={v} 
                        className="text-xs bg-background px-2 py-1 rounded border cursor-pointer hover:bg-primary/10"
                        onClick={() => {
                          navigator.clipboard.writeText(v);
                          toast.success(`${v} copié !`);
                        }}
                      >
                        {v}
                      </code>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Sujet de l'email</Label>
                  <Input
                    value={config.email_templates?.[selectedTemplate]?.subject || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      email_templates: {
                        ...config.email_templates,
                        [selectedTemplate]: {
                          ...config.email_templates?.[selectedTemplate],
                          subject: e.target.value
                        }
                      }
                    })}
                    placeholder="Sujet de l'email..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Corps de l'email (HTML)</Label>
                  <Textarea
                    value={config.email_templates?.[selectedTemplate]?.body || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      email_templates: {
                        ...config.email_templates,
                        [selectedTemplate]: {
                          ...config.email_templates?.[selectedTemplate],
                          body: e.target.value
                        }
                      }
                    })}
                    placeholder="Contenu HTML de l'email..."
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>

                {/* Preview */}
                <div className="space-y-2">
                  <Label>Aperçu</Label>
                  <div 
                    className="p-4 bg-white border rounded-xl prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ 
                      __html: (config.email_templates?.[selectedTemplate]?.body || '')
                        .replace(/\{\{client_name\}\}/g, 'Jean Dupont')
                        .replace(/\{\{client_email\}\}/g, 'jean@example.com')
                        .replace(/\{\{company_name\}\}/g, 'Les Jardins Verts')
                        .replace(/\{\{service_name\}\}/g, 'Entretien jardin')
                        .replace(/\{\{date\}\}/g, '25 janvier 2025')
                        .replace(/\{\{time\}\}/g, '09:00')
                        .replace(/\{\{price\}\}/g, '45.00')
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Trial Period Configuration */}
        <TabsContent value="trial">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    Période d'essai
                  </CardTitle>
                  <CardDescription>Configurez la durée de l'essai gratuit pour les nouvelles entreprises</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="trial-enabled">Activer</Label>
                  <Switch
                    id="trial-enabled"
                    checked={config.trial_enabled}
                    onCheckedChange={(checked) => setConfig({ ...config, trial_enabled: checked })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Durée de l'essai (jours)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    data-testid="trial-days"
                    type="number"
                    min="1"
                    max="90"
                    value={config.trial_days}
                    onChange={(e) => setConfig({ ...config, trial_days: parseInt(e.target.value) || 15 })}
                    disabled={!config.trial_enabled}
                    className="w-24"
                  />
                  <span className="text-muted-foreground">jours</span>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-4">
                {[7, 15, 30].map((days) => (
                  <button
                    key={days}
                    onClick={() => setConfig({ ...config, trial_days: days })}
                    disabled={!config.trial_enabled}
                    className={`p-4 rounded-xl border-2 transition-colors ${
                      config.trial_days === days 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    } ${!config.trial_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <p className="text-2xl font-bold">{days}</p>
                    <p className="text-sm text-muted-foreground">jours</p>
                  </button>
                ))}
              </div>

              {config.trial_enabled && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200 mt-4">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700">
                    Les nouvelles entreprises bénéficieront de {config.trial_days} jours d'essai gratuit
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Admin Tickets Page
function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [response, setResponse] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await adminApi.getTickets();
      setTickets(res.data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (ticketId, status, adminResponse = null) => {
    try {
      await adminApi.updateTicket(ticketId, { status, admin_response: adminResponse });
      toast.success('Ticket mis à jour');
      fetchTickets();
      setSelectedTicket(null);
      setResponse('');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open': return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100"><Clock className="w-3 h-3 mr-1" />Ouvert</Badge>;
      case 'in_progress': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Clock className="w-3 h-3 mr-1" />En cours</Badge>;
      case 'resolved': return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle2 className="w-3 h-3 mr-1" />Résolu</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredTickets = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);
  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Tickets Support</h1>
          <p className="text-muted-foreground">Gérez les demandes de support des utilisateurs</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="text-yellow-600">{openCount} ouverts</Badge>
          <Badge variant="outline" className="text-blue-600">{inProgressCount} en cours</Badge>
        </div>
      </div>

      <div className="flex gap-2">
        {['all', 'open', 'in_progress', 'resolved'].map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'Tous' : f === 'open' ? 'Ouverts' : f === 'in_progress' ? 'En cours' : 'Résolus'}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredTickets.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">Aucun ticket</h3>
            <p className="text-muted-foreground">Il n'y a pas de tickets dans cette catégorie</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((ticket) => (
            <Card key={ticket.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">
                        {ticket.category === 'bug' ? '🐛 Bug' : ticket.category === 'feature' ? '💡 Suggestion' : '❓ Question'}
                      </Badge>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <h3 className="font-semibold text-lg">{ticket.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{ticket.description}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <span>Client: {ticket.client_name || ticket.client_email || 'Anonyme'}</span>
                      <span>•</span>
                      <span>{new Date(ticket.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {ticket.status !== 'resolved' && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" onClick={() => setSelectedTicket(ticket)}>
                            <Send className="w-4 h-4 mr-1" />
                            Répondre
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Répondre au ticket</DialogTitle>
                            <DialogDescription>{ticket.title}</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div className="p-3 bg-muted rounded-lg">
                              <p className="text-sm">{ticket.description}</p>
                            </div>
                            <textarea
                              className="w-full p-3 border rounded-lg min-h-[100px]"
                              placeholder="Votre réponse..."
                              value={response}
                              onChange={(e) => setResponse(e.target.value)}
                            />
                            <div className="flex gap-2">
                              <Button 
                                className="flex-1"
                                onClick={() => handleUpdateStatus(ticket.id, 'in_progress', response)}
                              >
                                En cours
                              </Button>
                              <Button 
                                className="flex-1"
                                variant="default"
                                onClick={() => handleUpdateStatus(ticket.id, 'resolved', response)}
                              >
                                Résolu
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
                {ticket.admin_response && (
                  <div className="mt-4 p-3 bg-primary/5 rounded-lg border-l-2 border-primary">
                    <p className="text-xs text-muted-foreground mb-1">Réponse admin :</p>
                    <p className="text-sm">{ticket.admin_response}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Announcements Page
function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    message: '',
    link: '',
    link_text: '',
    type: 'info',
    start_date: '',
    end_date: '',
    is_active: true
  });

  const fetchAnnouncements = async () => {
    try {
      const res = await adminApi.getAnnouncements();
      setAnnouncements(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des annonces');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const resetForm = () => {
    setFormData({
      message: '',
      link: '',
      link_text: '',
      type: 'info',
      start_date: '',
      end_date: '',
      is_active: true
    });
    setEditingAnnouncement(null);
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      message: announcement.message || '',
      link: announcement.link || '',
      link_text: announcement.link_text || '',
      type: announcement.type || 'info',
      start_date: announcement.start_date || '',
      end_date: announcement.end_date || '',
      is_active: announcement.is_active !== false
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingAnnouncement) {
        await adminApi.updateAnnouncement(editingAnnouncement.id, formData);
        toast.success('Annonce mise à jour');
      } else {
        await adminApi.createAnnouncement(formData);
        toast.success('Annonce créée');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette annonce ?')) return;
    try {
      await adminApi.deleteAnnouncement(id);
      toast.success('Annonce supprimée');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (announcement) => {
    try {
      await adminApi.updateAnnouncement(announcement.id, {
        ...announcement,
        is_active: !announcement.is_active
      });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const TYPE_OPTIONS = [
    { value: 'info', label: 'Information', icon: Info, color: 'bg-blue-500' },
    { value: 'success', label: 'Succès', icon: CheckCircle2, color: 'bg-green-500' },
    { value: 'warning', label: 'Avertissement', icon: AlertTriangle, color: 'bg-amber-500' },
    { value: 'new_feature', label: 'Nouveauté', icon: Sparkles, color: 'bg-purple-500' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Annonces</h1>
          <p className="text-muted-foreground">Gérez les messages affichés aux entreprises</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle annonce
        </Button>
      </div>

      {/* Preview */}
      {announcements.filter(a => a.is_active).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Aperçu du bandeau</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className={`${TYPE_OPTIONS.find(t => t.value === announcements.find(a => a.is_active)?.type)?.color || 'bg-blue-500'} text-white py-2 px-4 text-center text-sm`}>
              {announcements.find(a => a.is_active)?.message}
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            </div>
          ) : announcements.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune annonce</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {announcements.map((announcement) => {
                  const typeInfo = TYPE_OPTIONS.find(t => t.value === announcement.type);
                  const TypeIcon = typeInfo?.icon || Info;
                  return (
                    <TableRow key={announcement.id}>
                      <TableCell>
                        <div className={`w-8 h-8 rounded-full ${typeInfo?.color || 'bg-blue-500'} flex items-center justify-center`}>
                          <TypeIcon className="w-4 h-4 text-white" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium line-clamp-1">{announcement.message}</p>
                        {announcement.link && (
                          <a href={announcement.link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                            {announcement.link_text || announcement.link}
                          </a>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {announcement.start_date || announcement.end_date ? (
                          <>
                            {announcement.start_date && <span>Du {announcement.start_date}</span>}
                            {announcement.end_date && <span> au {announcement.end_date}</span>}
                          </>
                        ) : (
                          <span>Permanent</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={announcement.is_active}
                          onCheckedChange={() => toggleActive(announcement)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(announcement)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(announcement.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAnnouncement ? 'Modifier l\'annonce' : 'Nouvelle annonce'}</DialogTitle>
            <DialogDescription>
              Ce message sera affiché en haut de l'espace entreprise
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                placeholder="Ex: Nouvelle fonctionnalité disponible ! Découvrez le widget Dispatcher..."
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lien (optionnel)</Label>
                <Input
                  placeholder="https://..."
                  value={formData.link}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Texte du lien</Label>
                <Input
                  placeholder="En savoir plus"
                  value={formData.link_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, link_text: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type d'annonce</Label>
              <div className="grid grid-cols-4 gap-2">
                {TYPE_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: option.value }))}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        formData.type === option.value
                          ? 'border-2 border-primary bg-primary/10'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full ${option.color} flex items-center justify-center mx-auto mb-1`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début (optionnel)</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin (optionnel)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label>Activer immédiatement</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={!formData.message}>
              {editingAnnouncement ? 'Enregistrer' : 'Créer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Roadmap Page - Internal Bug/Feature Tracking
function RoadmapPage() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'feature',
    priority: 'medium',
    status: 'todo',
    completed_version: ''
  });

  const fetchItems = async () => {
    try {
      const response = await adminApi.getRoadmapItems();
      setItems(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la roadmap');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        description: item.description || '',
        type: item.type,
        priority: item.priority,
        status: item.status,
        completed_version: item.completed_version || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        description: '',
        type: 'feature',
        priority: 'medium',
        status: 'todo',
        completed_version: ''
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }

    try {
      if (editingItem) {
        await adminApi.updateRoadmapItem(editingItem.id, formData);
        toast.success('Élément mis à jour');
      } else {
        await adminApi.createRoadmapItem(formData);
        toast.success('Élément créé');
      }
      setIsDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    try {
      await adminApi.deleteRoadmapItem(id);
      toast.success('Élément supprimé');
      fetchItems();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await adminApi.updateRoadmapItem(item.id, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchItems();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const filteredItems = items.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'bugs') return item.type === 'bug';
    if (filter === 'features') return item.type === 'feature' || item.type === 'improvement';
    return item.status === filter;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'todo':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">À faire</Badge>;
      case 'in_progress':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">En cours</Badge>;
      case 'done':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Terminé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'bug':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><Bug className="w-3 h-3 mr-1" />Bug</Badge>;
      case 'feature':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Lightbulb className="w-3 h-3 mr-1" />Fonctionnalité</Badge>;
      case 'improvement':
        return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100"><Sparkles className="w-3 h-3 mr-1" />Amélioration</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return <Badge variant="destructive" className="text-xs">Haute</Badge>;
      case 'medium':
        return <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Moyenne</Badge>;
      case 'low':
        return <Badge variant="outline" className="text-xs">Basse</Badge>;
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Stats
  const stats = {
    todo: items.filter(i => i.status === 'todo').length,
    in_progress: items.filter(i => i.status === 'in_progress').length,
    done: items.filter(i => i.status === 'done').length,
    bugs: items.filter(i => i.type === 'bug' && i.status !== 'done').length
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Roadmap & Bugs</h1>
          <p className="text-muted-foreground mt-1">Suivi du développement interne</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-roadmap-item-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel élément
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('todo')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <ListTodo className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.todo}</p>
                <p className="text-xs text-muted-foreground">À faire</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('in_progress')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Play className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.in_progress}</p>
                <p className="text-xs text-muted-foreground">En cours</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('done')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.done}</p>
                <p className="text-xs text-muted-foreground">Terminés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('bugs')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Bug className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.bugs}</p>
                <p className="text-xs text-muted-foreground">Bugs ouverts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button 
          variant={filter === 'all' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('all')}
        >
          Tous
        </Button>
        <Button 
          variant={filter === 'bugs' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('bugs')}
        >
          <Bug className="w-3 h-3 mr-1" />
          Bugs
        </Button>
        <Button 
          variant={filter === 'features' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('features')}
        >
          <Lightbulb className="w-3 h-3 mr-1" />
          Fonctionnalités
        </Button>
        <Button 
          variant={filter === 'todo' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('todo')}
        >
          À faire
        </Button>
        <Button 
          variant={filter === 'in_progress' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('in_progress')}
        >
          En cours
        </Button>
        <Button 
          variant={filter === 'done' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('done')}
        >
          Terminés
        </Button>
      </div>

      {/* Items List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Chargement...</div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <ListTodo className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Aucun élément trouvé</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Priorité</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{item.title}</p>
                        {item.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-xs">{item.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(item.type)}</TableCell>
                    <TableCell>{getPriorityBadge(item.priority)}</TableCell>
                    <TableCell>{getStatusBadge(item.status)}</TableCell>
                    <TableCell>
                      <div className="text-xs">
                        {item.start_date && (
                          <p className="text-muted-foreground">Début: {formatDate(item.start_date)}</p>
                        )}
                        {item.end_date && (
                          <p className="text-green-600">Fin: {formatDate(item.end_date)}</p>
                        )}
                        {!item.start_date && !item.end_date && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.completed_version ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          v{item.completed_version}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {item.status === 'todo' && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleStatusChange(item, 'in_progress')}
                            title="Démarrer"
                          >
                            <Play className="w-4 h-4 text-amber-600" />
                          </Button>
                        )}
                        {item.status === 'in_progress' && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleStatusChange(item, 'done')}
                            title="Terminer"
                          >
                            <CheckCheck className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleOpenDialog(item)}
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(item.id)}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifier' : 'Nouvel élément'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Modifier les détails de cet élément' : 'Ajouter un bug ou une fonctionnalité à la roadmap'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Corriger le bug de connexion..."
                data-testid="roadmap-item-title"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Détails supplémentaires..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">
                      <div className="flex items-center gap-2">
                        <Bug className="w-4 h-4 text-red-600" />
                        Bug
                      </div>
                    </SelectItem>
                    <SelectItem value="feature">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-blue-600" />
                        Fonctionnalité
                      </div>
                    </SelectItem>
                    <SelectItem value="improvement">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Amélioration
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Priorité</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Basse</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Statut</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">À faire</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="done">Terminé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Version de livraison</Label>
                <Input
                  value={formData.completed_version}
                  onChange={(e) => setFormData({ ...formData, completed_version: e.target.value })}
                  placeholder="Ex: 4.15.0"
                  disabled={formData.status !== 'done'}
                />
                {formData.status !== 'done' && (
                  <p className="text-xs text-muted-foreground mt-1">Disponible uniquement pour les éléments terminés</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSave} data-testid="save-roadmap-item-btn">
                {editingItem ? 'Enregistrer' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Admin Dashboard Layout
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Déconnexion réussie');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { path: '/admin/roadmap', icon: ListTodo, label: 'Roadmap & Bugs' },
    { path: '/admin/announcements', icon: Megaphone, label: 'Annonces' },
    { path: '/admin/tickets', icon: MessageSquare, label: 'Tickets Support' },
    { path: '/admin/licenses', icon: Key, label: 'Licences' },
    { path: '/admin/invoices', icon: Receipt, label: 'Facturation' },
    { path: '/admin/promo-codes', icon: Tag, label: 'Codes Promo' },
    { path: '/admin/companies', icon: Building2, label: 'Entreprises' },
    { path: '/admin/branding', icon: Palette, label: 'Personnalisation' },
    { path: '/admin/configuration', icon: Settings, label: 'Configuration' },
  ];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile Header */}
      <header className="lg:hidden flex items-center justify-between p-4 bg-card border-b border-border sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <img 
            src="/logo.png" 
            alt="ResaWork" 
            className="h-7"
          />
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[57px] bg-background/95 backdrop-blur-sm z-40 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
            <div className="pt-4 mt-4 border-t border-border">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-muted-foreground hover:text-destructive"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 bg-card border-r border-border p-6 flex-col">
        <div className="flex items-center gap-3 mb-8">
          <img 
            src="/logo.png" 
            alt="ResaWork" 
            className="h-11"
          />
        </div>

        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="pt-6 border-t border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
              <span className="text-sm font-medium">{user?.name?.charAt(0) || 'A'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-muted-foreground">Administrateur</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            data-testid="logout-btn"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
          <div className="mt-4 pt-4 border-t">
            <FooterMinimal />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Desktop Top Bar with Notifications */}
        <div className="hidden lg:flex items-center justify-end gap-4 p-4 border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-20">
          <NotificationBell />
          <div className="flex items-center gap-3 px-3 py-1.5 rounded-lg bg-muted/50">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-primary">{user?.name?.charAt(0) || 'A'}</span>
            </div>
            <span className="text-sm font-medium">{user?.name || 'Admin'}</span>
          </div>
        </div>
        <div className="p-4 lg:p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="roadmap" element={<RoadmapPage />} />
            <Route path="announcements" element={<AnnouncementsPage />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="licenses" element={<LicensesPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="promo-codes" element={<PromoCodesPage />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="impersonation-audit" element={<ImpersonationAuditPage />} />
            <Route path="branding" element={<BrandingPage />} />
            <Route path="configuration" element={<ConfigurationPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
